import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Type, TypeContextAPI } from "./type";
import typeService from "./typeService";
const TypeContext = createContext<TypeContextAPI>({} as TypeContextAPI);
const handleHref = (href: string) =>
  href.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [types, setTypes] = useState<Type[]>([]);
  const [type, setType] = useState<Type>({ _id: "", name: "" });
  const pending = useRef<Promise<void>>();
  const getAllTypes = useCallback(() => {
    if (pending.current) return pending.current;
    setLoading(true);
    setError(undefined);
    pending.current = typeService
      .getAllType()
      .then((res) => setTypes(res.data))
      .catch((err) => setError(String(err)))
      .finally(() => {
        pending.current = undefined;
        setLoading(false);
      });
    return pending.current;
  }, []);
  const addNewType = useCallback((item: Type) => {
    setLoading(true);
    setError(undefined);
    return typeService
      .addNewType(item)
      .then((res) => {
        setType(res.data);
        setTypes((previous) => [...previous, res.data]);
      })
      .catch((err) => setError(String(err)))
      .finally(() => setLoading(false));
  }, []);
  const updateType = useCallback(
    (item: Type) => {
      setLoading(true);
      setError(undefined);
      return typeService
        .updateType(item)
        .then(() => getAllTypes())
        .catch((err) => setError(String(err)))
        .finally(() => setLoading(false));
    },
    [getAllTypes]
  );
  const deleteType = useCallback((id: string) => {
    setLoading(true);
    setError(undefined);
    return typeService
      .destroyType(id)
      .then(() =>
        setTypes((previous) => previous.filter((item) => item._id !== id))
      )
      .catch((err) => setError(String(err)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    getAllTypes();
  }, [getAllTypes]);
  const value = useMemo(
    () => ({
      loading,
      error,
      type,
      setType,
      types,
      addNewType,
      getAllTypes,
      handleHref,
      deleteType,
      updateType,
    }),
    [
      loading,
      error,
      type,
      types,
      addNewType,
      getAllTypes,
      deleteType,
      updateType,
    ]
  );
  return <TypeContext.Provider value={value}>{children}</TypeContext.Provider>;
};
export const useType = () => useContext(TypeContext);
export default Provider;
